import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Car, Loader2, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { apiError } from "@/lib/api";

const DEMO_ACCOUNT = { email: "owner@caros.de", password: "Demo2024!" };

export default function LoginPage() {
  const { login } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate("/app");
    } catch (err) {
      setError(apiError(err, "Anmeldung fehlgeschlagen"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:flex flex-col justify-between p-12 hero-sheen border-r border-border overflow-hidden">
        <div className="noise-overlay" />
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 border border-primary/30">
            <Car className="h-5 w-5 text-primary" />
          </div>
          <span className="font-display text-2xl font-bold">CarOS</span>
        </div>
        <div className="space-y-5 max-w-lg">
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight">
            Einmal aufnehmen.<br />Automatisch verstehen.<br />Überall verkaufen.
          </h1>
          <p className="text-muted-foreground text-lg">
            Vom Fahrzeugankauf zum Inserat in wenigen Minuten. VIN scannen, Fotos aufnehmen,
            Notizen diktieren – CarOS erledigt den Rest.
          </p>
          <div className="flex gap-6 text-sm text-muted-foreground pt-2">
            <div><span className="font-display text-2xl text-foreground font-bold block">≤ 15 min</span>Vehicle-to-Market</div>
            <div><span className="font-display text-2xl text-foreground font-bold block">1×</span>Datenerfassung</div>
            <div><span className="font-display text-2xl text-foreground font-bold block">100%</span>Nachvollziehbar</div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Das KI-Betriebssystem für moderne Autohändler</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-6">
          <div className="lg:hidden flex items-center gap-2 justify-center">
            <Car className="h-6 w-6 text-primary" />
            <span className="font-display text-2xl font-bold">CarOS</span>
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold">{t("login.title")}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t("login.subtitle")}</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">{t("login.email")}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="owner@caros.de" required data-testid="tenant-login-email-input" className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{t("login.password")}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required data-testid="tenant-login-password-input" className="h-11" />
            </div>
            {error && (
              <p className="text-sm text-red-400" data-testid="login-error">{error}</p>
            )}
            <Button type="submit" className="w-full h-11 font-semibold" disabled={busy} data-testid="tenant-login-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("login.submit")}
            </Button>
          </form>

          <Card className="bg-card/60 border-primary/25">
            <CardContent className="pt-4 pb-4 space-y-2">
              <Button type="button" variant="outline" className="w-full h-11 gap-2 border-primary/40 text-primary hover:bg-primary/10 font-semibold"
                data-testid="demo-login-button"
                onClick={() => { setEmail(DEMO_ACCOUNT.email); setPassword(DEMO_ACCOUNT.password); setError(null); }}>
                <PlayCircle className="h-4 w-4" /> Demo testen
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">
                Füllt Zugangsdaten des Demo-Autohauses mit Beispielfahrzeugen aus – danach einfach auf „Anmelden" klicken.
              </p>
            </CardContent>
          </Card>

          <div className="text-center space-y-1">
            <Link to="/registrieren" className="text-xs text-primary hover:underline block" data-testid="login-register-link">
              Noch kein Zugang? Pilotpartner werden – 199 €/Monat, jederzeit kündbar
            </Link>
            <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" data-testid="tenant-login-language-toggle"
              onClick={() => setLang(lang === "de" ? "en" : "de")}>
              {lang === "de" ? "Switch to English" : "Auf Deutsch wechseln"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
